import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useProjectStore } from '../../../store/projectStore';
import { useAuthStore } from '../../../store/authStore';
import { useSitesStore } from '../../../store/sitesStore';
import { StockRequest, formatRequestRef, useStockRequestStore } from '../../../store/stockRequestStore';
import { isReviewAssignment } from '../../../store/stockRequestAssignment';
import { getRequestTheme, statusBadge, fmtRequestStatusLabel, requestStatusBadgeClass } from './theme';
import ChatPanel from '../../common/ChatPanel';
import RequestExpandedPanel from './RequestExpandedPanel';
import RequestJobLinkBar from './RequestJobLinkBar';
import type { RequestsSubTab } from './RequestsView';

interface Props {
  requestId: string;
  subTab: RequestsSubTab;
  initialRequest: StockRequest;
  destination: string;
  requesterName: string;
  onClose: () => void;
  onRequestUpdated?: () => void;
}

const RequestDetailModal: React.FC<Props> = ({
  requestId,
  subTab,
  initialRequest,
  destination,
  requesterName,
  onClose,
  onRequestUpdated,
}) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const { user } = useAuthStore();
  const { sites } = useSitesStore();
  const storeRequest = useStockRequestStore(s => s.requests.find(r => r.id === requestId));

  const request = storeRequest ?? initialRequest;
  const isAssignedToMe = !request.assignedTo || request.assignedTo === user?.id;
  const canReview = subTab === 'to_review' && isAssignedToMe
    && isReviewAssignment(request.assignmentStep, request.status)
    && (request.status === 'submitted' || request.status === 'approved');
  const canEditLinks = !['cancelled', 'completed'].includes(request.status)
    && (request.requesterId === user?.id || canReview);

  const headerStatus = useMemo(
    () => statusBadge(request.status, isDark),
    [request.status, isDark],
  );

  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  const close = () => { setVisible(false); setTimeout(onClose, 180); };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className="fixed inset-0 z-[60] transition-opacity duration-150"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
        <div
          className="pointer-events-auto flex flex-col overflow-hidden rounded-lg border shadow-2xl transition-all duration-150"
          style={{
            width: '90vw',
            maxWidth: '1280px',
            height: '85vh',
            backgroundColor: theme.cardBg,
            borderColor: theme.border,
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.98)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3 flex-shrink-0"
            style={{ borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide" style={{ color: theme.muted }}>
                  Stock Request · {destination}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <h2 className="text-base font-semibold truncate" style={{ color: theme.text }}>
                    {formatRequestRef(request.requestNumber)}
                  </h2>
                  <span className={`${requestStatusBadgeClass} ${headerStatus}`}>
                    {fmtRequestStatusLabel(request.status)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-sm" style={{ color: theme.muted }}>
                Requested by <span style={{ color: theme.text }}>{requesterName}</span>
              </span>
              <button
                onClick={close}
                className="p-1.5 rounded transition-colors"
                style={{ color: theme.muted }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body — two columns: details + related job / chat */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto py-3 min-w-0" style={{ backgroundColor: theme.subTableBg }}>
              <RequestExpandedPanel
                requestId={requestId}
                subTab={subTab}
                initialRequest={initialRequest}
                onRequestUpdated={onRequestUpdated}
              />
            </div>

            <div
              className="flex flex-col flex-shrink-0 px-4 py-3 min-h-0"
              style={{ width: 340, borderLeft: `1px solid ${theme.border}`, backgroundColor: theme.subTableBg }}
            >
              <div className="flex-shrink-0 mb-3">
                <RequestJobLinkBar
                  request={request}
                  sites={sites}
                  canEdit={canEditLinks}
                  theme={theme}
                  onUpdated={onRequestUpdated}
                />
              </div>

              <div
                className="flex-shrink-0 mb-3 -mx-4"
                style={{ borderTop: `1px solid ${theme.border}` }}
              />

              <p className="text-[11px] uppercase tracking-wide font-medium mb-2 flex-shrink-0" style={{ color: theme.muted }}>
                Chat
              </p>
              <div className="flex-1 min-h-0">
                <ChatPanel
                  contextType="stock_request"
                  contextId={requestId}
                  isDark={isDark}
                  theme={{
                    text: theme.text,
                    muted: theme.muted,
                    border: theme.border,
                    inputBg: theme.inputBg,
                    cardBg: theme.subTableBg,
                  }}
                  emptyLabel="No messages yet. Use this to discuss the request with the reviewer."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestDetailModal;
